import type { ResourceObject } from './JsonApi'

// Vendored, dependency-free port of `deserialize-json-api@1.4.0` — the version
// Manager View ran in production via @attensi/v3-client-wip.
//
// Why vendor rather than keep depending on the package: it is effectively
// unmaintained, and its only newer release — 2.0.0 — is unsafe for us. That
// release removed the recursion guard and stack-overflows on self-referential
// resources such as self-approval, while still not resolving relationships
// from `data`. Owning the code lets us fix the long-running FAME-272
// relationship-resolution bug directly and review the transformation in-tree.
// Given that trajectory, if this is ever revisited, a maintained JSON:API
// library is a safer destination than returning to the package.
//
// Only the relationship-resolution core is ported. The library's optional
// `camelCase` key transform is intentionally omitted: `unravel()` never passed
// transform options, so that path was dead code whose sole purpose was to pull
// in three lodash micro-dependencies. The v3 API returns snake_case keys and
// the app reads them as-is.
//
// The graph being traversed is dynamic JSON:API data, so the internals operate
// on loosely-typed records rather than a closed type: JSON:API resource objects
// carry arbitrary attribute/relationship values a closed type cannot model.
// biome-ignore lint/suspicious/noExplicitAny: dynamic JSON:API resource graph.
type AnyResource = Record<string, any>

// `Object.hasOwn()` (biome's preferred form) requires the ES2022 lib, but the
// project targets ES2020 (see @vue/tsconfig), so use the portable prototype
// call. Wrapped so the single suppression lives in one place.
const hasOwnKey = (object: AnyResource, key: string): boolean =>
  // biome-ignore lint/suspicious/noPrototypeBuiltins: Object.hasOwn needs ES2022; project targets ES2020.
  Object.prototype.hasOwnProperty.call(object, key)

const asAttributes = ({ attributes, ...rest }: AnyResource): AnyResource => {
  if (!attributes || attributes.constructor !== Object) return rest
  // Attributes spread last so a relationship-identifier `type`/`id` can be
  // overridden by the resource's own attributes, matching the spec example.
  return { ...rest, ...attributes }
}

const flattenAttributes = (data: AnyResource): AnyResource => {
  if (!data || typeof data !== 'object') return {}
  if (Array.isArray(data)) return data.map(asAttributes)
  return asAttributes(data)
}

const findResource = (
  rel: AnyResource,
  included: AnyResource[] | undefined
): AnyResource | undefined => {
  if (!Array.isArray(included)) return undefined
  return included.find((res) => res.id === rel.id && res.type === rel.type)
}

const withoutRelationships = ({
  relationships,
  ...rest
}: AnyResource): AnyResource => rest

// Resolves a single relationship identifier against the pool. Returns the
// resolved resource (or `undefined` if absent) together with a pool copy in
// which the matched resource has had its `relationships` stripped. Threading
// that stripped copy forward is the library's recursion guard: it terminates
// circular and self-references (e.g. self-approval) instead of overflowing.
const deserializeIncluded = (
  rel: AnyResource,
  included: AnyResource[] | undefined
): [AnyResource | undefined, AnyResource[] | undefined] => {
  const resource = findResource(rel, included)
  if (!resource || !included) return [undefined, included]

  const filteredIncluded = included.map((res) =>
    res === resource ? withoutRelationships(resource) : res
  )

  return [{ ...rel, ...flattenAttributes(resource) }, filteredIncluded]
}

const mapRelationships = (
  resource: AnyResource,
  included: AnyResource[] | undefined
): AnyResource => {
  let { relationships, ...result } = resource

  if (hasOwnKey(resource, 'attributes')) {
    result = flattenAttributes(result)
  }

  for (const key in relationships) {
    if (hasOwnKey(result, key)) continue

    const relData = relationships[key].data
    let deserializedRel: AnyResource | AnyResource[] | undefined

    if (relData && Array.isArray(relData)) {
      let includedRels: AnyResource[] = []
      relData.forEach((rel: AnyResource) => {
        let dRel: AnyResource | undefined
        ;[dRel, included] = deserializeIncluded(rel, included)
        if (dRel) includedRels.push(dRel)
      })

      includedRels = includedRels.map((rel) => mapRelationships(rel, included))

      if (includedRels.length) deserializedRel = includedRels
    } else if (relData) {
      const [dRel, filteredIncluded] = deserializeIncluded(relData, included)
      if (dRel) {
        deserializedRel = mapRelationships(dRel, filteredIncluded)
      }
    }

    if (deserializedRel) result[key] = deserializedRel
  }

  return result
}

interface CompoundDocument {
  data?: ResourceObject | ResourceObject[]
  included?: ResourceObject[]
}

// Resolves every resource's relationships against `included` and returns the
// flattened primary data (the value `unravel()` exposes to callers).
export const deserialize = (response: CompoundDocument): unknown => {
  const { data, included } = response

  if (Array.isArray(data)) {
    return data.map((resource) => mapRelationships(resource, included))
  }

  return mapRelationships(data ?? {}, included)
}
